import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import orangeLogo from '../assets/orangelog.png';

const TOC = [
  { id: 'intro',       label: 'Introduction' },
  { id: 'collect',     label: 'Information We Collect' },
  { id: 'use',         label: 'How We Use Your Information' },
  { id: 'share',       label: 'How We Share Your Information' },
  { id: 'cookies',     label: 'Cookies and Local Storage' },
  { id: 'retention',   label: 'Data Retention' },
  { id: 'rights',      label: 'Your Rights and Choices' },
  { id: 'children',    label: "Children's Privacy" },
  { id: 'security',    label: 'Security' },
  { id: 'thirdparty',  label: 'Third-Party Links' },
  { id: 'changes',     label: 'Changes to This Policy' },
  { id: 'contact-priv', label: 'Contact Us' },
];

const S = {
  h2:  { fontSize: '20px', fontWeight: '900', color: '#f0f4ff', fontFamily: 'Georgia, serif', margin: '0 0 14px' },
  h3:  { fontSize: '14px', fontWeight: '800', color: '#c8d8e8', fontFamily: 'Arial, sans-serif', margin: '20px 0 7px', textTransform: 'uppercase', letterSpacing: '0.06em' },
  p:   { fontSize: '14px', color: '#7a8fa8', lineHeight: '1.85', margin: '0 0 14px', fontFamily: 'Arial, sans-serif' },
  li:  { fontSize: '14px', color: '#7a8fa8', lineHeight: '1.85', fontFamily: 'Arial, sans-serif', marginBottom: '6px' },
  ul:  { margin: '0 0 16px', paddingLeft: '22px' },
  hr:  { height: '1px', background: '#0e1a28', border: 'none', margin: '28px 0' },
  sec: { scrollMarginTop: '80px', marginBottom: '8px' },
};

export default function PrivacyPage() {
  const [isMobile, setIsMobile] = useState(() => window.innerWidth < 900);
  useEffect(() => {
    const h = () => setIsMobile(window.innerWidth < 900);
    window.addEventListener('resize', h);
    return () => window.removeEventListener('resize', h);
  }, []);

  const scrollTo = (id) => document.getElementById(id)?.scrollIntoView({ behavior: 'smooth', block: 'start' });

  return (
    <>
    <Helmet>
      <title>Privacy Policy — PubSilo</title>
      <meta name="description" content="PubSilo Privacy Policy — how we collect, use, and protect your information." />
    </Helmet>
    <div style={{ minHeight: '100vh', background: '#06090f', fontFamily: 'Arial, sans-serif' }}>

      {/* Header */}
      <div style={{
        background: '#040710', borderBottom: '1px solid #0e1a28',
        padding: '14px 40px', display: 'flex', alignItems: 'center', justifyContent: 'space-between',
        position: 'sticky', top: 0, zIndex: 100,
      }}>
        <Link to="/" style={{ textDecoration: 'none' }}>
          <div style={{ background: '#F41238', borderRadius: '8px', padding: '7px 16px', display: 'inline-block' }}>
            <img src={orangeLogo} alt="PubSilo" style={{ height: '28px', objectFit: 'contain', display: 'block' }} />
          </div>
        </Link>
        <div style={{ fontSize: '13px', fontWeight: '700', color: '#4a6a8a', letterSpacing: '0.1em', textTransform: 'uppercase' }}>
          Privacy Policy
        </div>
        <Link to="/" style={{ fontSize: '13px', color: '#4a6a8a', textDecoration: 'none' }}
          onMouseEnter={e => e.currentTarget.style.color = '#c8d8e8'}
          onMouseLeave={e => e.currentTarget.style.color = '#4a6a8a'}
        >← Back to PubSilo</Link>
      </div>

      {/* Page title */}
      <div style={{ background: 'linear-gradient(to bottom, #0d1320, #06090f)', padding: '48px 40px 32px', maxWidth: '1100px', margin: '0 auto' }}>
        <div style={{ fontSize: '11px', fontWeight: '800', letterSpacing: '0.22em', textTransform: 'uppercase', color: '#4a6a8a', marginBottom: '10px' }}>Legal</div>
        <h1 style={{ fontSize: 'clamp(26px, 3vw, 38px)', fontWeight: '900', color: '#f0f4ff', fontFamily: 'Georgia, serif', margin: '0 0 10px' }}>Privacy Policy</h1>
        <div style={{ fontSize: '13px', color: '#3a5570' }}>Effective Date: May 17, 2026 &nbsp;·&nbsp; Last Updated: May 17, 2026</div>
      </div>

      {/* Two-column layout */}
      <div style={{ maxWidth: '1100px', margin: '0 auto', padding: '0 40px 80px', display: 'flex', gap: '60px', alignItems: 'flex-start' }}>

        {/* Sticky TOC */}
        {!isMobile && (
          <div style={{ width: '190px', flexShrink: 0 }}>
            <div style={{ position: 'sticky', top: '80px', paddingTop: '20px' }}>
              <div style={{ fontSize: '10px', fontWeight: '800', letterSpacing: '0.2em', textTransform: 'uppercase', color: '#3a5570', marginBottom: '14px' }}>
                Contents
              </div>
              {TOC.map((item, i) => (
                <button
                  key={item.id}
                  onClick={() => scrollTo(item.id)}
                  style={{
                    display: 'block', width: '100%', background: 'none', border: 'none', cursor: 'pointer',
                    padding: '5px 0', textAlign: 'left', fontSize: '12px', color: '#4a6a8a',
                    fontFamily: 'Arial, sans-serif', transition: 'color 0.15s',
                  }}
                  onMouseEnter={e => e.currentTarget.style.color = '#c8d8e8'}
                  onMouseLeave={e => e.currentTarget.style.color = '#4a6a8a'}
                >
                  {i + 1}. {item.label}
                </button>
              ))}
            </div>
          </div>
        )}

        {/* Document body */}
        <div style={{ flex: 1, minWidth: 0, paddingTop: '20px' }}>

          <hr style={S.hr} />
          <div id="intro" style={S.sec}>
            <h2 style={S.h2}>1. Introduction</h2>
            <p style={S.p}>PubSilo, LLC ("PubSilo," "we," "us," or "our") is committed to protecting your privacy. This Privacy Policy explains how we collect, use, share, and protect information when you use PubSilo.com and related services (the "Service"). By using the Service, you agree to the practices described in this policy.</p>
          </div>

          <hr style={S.hr} />
          <div id="collect" style={S.sec}>
            <h2 style={S.h2}>2. Information We Collect</h2>
            <h3 style={S.h3}>2.1 Account Information</h3>
            <p style={S.p}>When you register, we collect your username, email address, and a securely hashed version of your password. Publisher accounts additionally collect company name, billing contact, and company details provided during onboarding.</p>
            <h3 style={S.h3}>2.2 Profile and Preferences</h3>
            <p style={S.p}>We store the reading interests, genre preferences, and location information you provide when setting up your profile. This information is used to personalize your content feed and is updated whenever you edit your settings.</p>
            <h3 style={S.h3}>2.3 Transaction Data</h3>
            <p style={S.p}>We maintain records of token purchases, article access (which articles you have unlocked and when), and refund transactions. Payment processing is handled by a third-party processor. We do not store your raw card number, CVV, or full payment credentials on our servers.</p>
            <h3 style={S.h3}>2.4 Usage Data</h3>
            <p style={S.p}>We collect information about how you interact with the Service, including pages visited, articles viewed, search terms, clicks, and session duration. This data is used in aggregate to improve the Service and is not used to build individual advertising profiles.</p>
            <h3 style={S.h3}>2.5 Device and Log Data</h3>
            <p style={S.p}>We automatically log IP addresses, browser type, operating system, referring URLs, and similar technical information when you access the Service. This data is used for security monitoring, rate limiting, fraud prevention, and troubleshooting.</p>
          </div>

          <hr style={S.hr} />
          <div id="use" style={S.sec}>
            <h2 style={S.h2}>3. How We Use Your Information</h2>
            <p style={S.p}>We use the information we collect to:</p>
            <ul style={S.ul}>
              <li style={S.li}>Create and manage your account</li>
              <li style={S.li}>Process token purchases and article access transactions</li>
              <li style={S.li}>Personalize your content feed and article recommendations</li>
              <li style={S.li}>Send transactional emails (email verification, password reset, purchase confirmation, payout notices)</li>
              <li style={S.li}>Detect and prevent fraud, abuse, and security incidents</li>
              <li style={S.li}>Enforce our Terms of Service and content standards</li>
              <li style={S.li}>Comply with legal obligations</li>
              <li style={S.li}>Operate, maintain, and improve the Service</li>
            </ul>
            <p style={S.p}>We do not use your personal information for third-party advertising targeting.</p>
          </div>

          <hr style={S.hr} />
          <div id="share" style={S.sec}>
            <h2 style={S.h2}>4. How We Share Your Information</h2>
            <h3 style={S.h3}>4.1 Service Providers</h3>
            <p style={S.p}>We share data with trusted vendors who help us operate the Service, including:</p>
            <ul style={S.ul}>
              <li style={S.li}><strong style={{ color: '#c8d8e8' }}>MongoDB Atlas</strong> — cloud database hosting</li>
              <li style={S.li}><strong style={{ color: '#c8d8e8' }}>Cloudinary</strong> — image storage and delivery</li>
              <li style={S.li}><strong style={{ color: '#c8d8e8' }}>Stripe</strong> — payment processing</li>
              <li style={S.li}><strong style={{ color: '#c8d8e8' }}>Microsoft / Office 365</strong> — transactional email delivery</li>
            </ul>
            <p style={S.p}>Each vendor is contractually obligated to use your data only as needed to perform services for us and may not use it for their own marketing purposes.</p>
            <h3 style={S.h3}>4.2 Publishers (External Link Articles)</h3>
            <p style={S.p}>When you purchase an article that redirects to a publisher's own website, a secure, time-limited access token is passed to the publisher's server solely to verify your purchase. No personally identifiable information such as your name or email address is transmitted to the publisher without your explicit consent.</p>
            <h3 style={S.h3}>4.3 Legal Requirements</h3>
            <p style={S.p}>We may disclose your information if required by law, subpoena, court order, or to protect the rights, property, or safety of PubSilo, our users, or the public.</p>
            <h3 style={S.h3}>4.4 No Sale of Personal Data</h3>
            <p style={S.p}>We do not sell, rent, or trade your personal information to third parties for their own marketing or commercial purposes.</p>
          </div>

          <hr style={S.hr} />
          <div id="cookies" style={S.sec}>
            <h2 style={S.h2}>5. Cookies and Local Storage</h2>
            <p style={S.p}>PubSilo uses the following browser storage mechanisms:</p>
            <ul style={S.ul}>
              <li style={S.li}><strong style={{ color: '#c8d8e8' }}>HttpOnly authentication cookies</strong> — used to keep you securely logged in. These are necessary for the Service to function and cannot be disabled while using PubSilo.</li>
              <li style={S.li}><strong style={{ color: '#c8d8e8' }}>Local storage</strong> — used to persist non-sensitive UI preferences such as your last-visited section or display settings.</li>
            </ul>
            <p style={S.p}>We do not use advertising cookies, third-party tracking pixels, or any cookies that identify you personally for marketing purposes.</p>
          </div>

          <hr style={S.hr} />
          <div id="retention" style={S.sec}>
            <h2 style={S.h2}>6. Data Retention</h2>
            <p style={S.p}>We retain your account information for as long as your account is active or as needed to provide the Service. Transaction records are retained for a minimum of seven years for financial and tax compliance, even after account deletion. Log and security data is retained for up to 90 days.</p>
            <p style={S.p}>When you request account deletion, we will remove your personal profile data within a reasonable timeframe while retaining only what is required for legal, financial, or safety compliance.</p>
          </div>

          <hr style={S.hr} />
          <div id="rights" style={S.sec}>
            <h2 style={S.h2}>7. Your Rights and Choices</h2>
            <h3 style={S.h3}>7.1 Access and Correction</h3>
            <p style={S.p}>You can view and update your profile, interests, and preferences from your dashboard at any time without contacting us.</p>
            <h3 style={S.h3}>7.2 Account Deletion</h3>
            <p style={S.p}>You may request account deletion by emailing support@pubsilo.com. We will process your request within a reasonable timeframe. Note that transaction records required for financial compliance will be retained even after your account is deleted, per Section 6.</p>
            <h3 style={S.h3}>7.3 Email Opt-Out</h3>
            <p style={S.p}>You may opt out of non-essential marketing emails by using the unsubscribe link in any such email. Transactional emails (security alerts, password resets, purchase confirmations) cannot be disabled while your account is active, as they are necessary for account operation.</p>
            <h3 style={S.h3}>7.4 California Residents (CCPA)</h3>
            <p style={S.p}>California residents have the right to know what personal information we collect, request deletion of their data, and opt out of the sale of personal information. PubSilo does not sell personal information. To exercise your rights, contact admin@pubsilo.com.</p>
            <h3 style={S.h3}>7.5 European Users (GDPR)</h3>
            <p style={S.p}>If you are located in the European Economic Area, you have the right to access, rectify, erase, restrict processing of, and port your personal data, as well as the right to object to certain processing. To make a request, contact admin@pubsilo.com. Please note that PubSilo's servers and primary data processors are located in the United States.</p>
          </div>

          <hr style={S.hr} />
          <div id="children" style={S.sec}>
            <h2 style={S.h2}>8. Children's Privacy</h2>
            <p style={S.p}>PubSilo is not directed to children under the age of 13, and we do not knowingly collect personal information from children under 13. If you believe we have inadvertently collected such information, please contact us at admin@pubsilo.com and we will delete it promptly.</p>
          </div>

          <hr style={S.hr} />
          <div id="security" style={S.sec}>
            <h2 style={S.h2}>9. Security</h2>
            <p style={S.p}>PubSilo uses industry-standard security practices including HTTPS encryption in transit, bcrypt password hashing, HttpOnly JWT cookies to protect authentication credentials, and server-side rate limiting to prevent abuse. No method of electronic transmission or storage is 100% secure. While we work hard to protect your information, we cannot guarantee absolute security.</p>
            <p style={S.p}>If you believe your account has been compromised, contact us immediately at support@pubsilo.com.</p>
          </div>

          <hr style={S.hr} />
          <div id="thirdparty" style={S.sec}>
            <h2 style={S.h2}>10. Third-Party Links</h2>
            <p style={S.p}>The Service may contain links to publisher websites and other third-party content. This Privacy Policy does not apply to those external sites. PubSilo has no control over and assumes no responsibility for the privacy practices of third-party sites. We encourage you to review the privacy policy of any third-party site you visit.</p>
          </div>

          <hr style={S.hr} />
          <div id="changes" style={S.sec}>
            <h2 style={S.h2}>11. Changes to This Policy</h2>
            <p style={S.p}>We may update this Privacy Policy periodically to reflect changes in our practices or legal requirements. If we make material changes, we will notify you by email or by posting a prominent notice on the Service. The "Last Updated" date at the top of this page reflects when the most recent changes were made. Continued use of the Service after the updated effective date constitutes acceptance of the revised policy.</p>
          </div>

          <hr style={S.hr} />
          <div id="contact-priv" style={S.sec}>
            <h2 style={S.h2}>12. Contact Us</h2>
            <p style={S.p}>For privacy-related questions, data requests, or concerns:</p>
            <p style={S.p}>
              PubSilo, LLC<br />
              <a href="mailto:admin@pubsilo.com" style={{ color: '#F41238', textDecoration: 'none' }}>admin@pubsilo.com</a> — privacy requests &amp; data inquiries<br />
              <a href="mailto:support@pubsilo.com" style={{ color: '#F41238', textDecoration: 'none' }}>support@pubsilo.com</a> — general account support
            </p>
          </div>

        </div>
      </div>

      {/* Footer */}
      <div style={{ borderTop: '1px solid #0e1a28', padding: '20px 40px', display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: '12px', background: '#040710' }}>
        <div style={{ fontSize: '13px', color: '#2a4060' }}>© {new Date().getFullYear()} PubSilo, LLC</div>
        <div style={{ display: 'flex', gap: '24px' }}>
          <Link to="/help" style={{ fontSize: '13px', color: '#3a6080', textDecoration: 'none' }}
            onMouseEnter={e => e.currentTarget.style.color = '#c8d8e8'}
            onMouseLeave={e => e.currentTarget.style.color = '#3a6080'}
          >Help Center</Link>
          <Link to="/terms" style={{ fontSize: '13px', color: '#3a6080', textDecoration: 'none' }}
            onMouseEnter={e => e.currentTarget.style.color = '#c8d8e8'}
            onMouseLeave={e => e.currentTarget.style.color = '#3a6080'}
          >Terms of Service</Link>
          <Link to="/" style={{ fontSize: '13px', color: '#3a6080', textDecoration: 'none' }}
            onMouseEnter={e => e.currentTarget.style.color = '#c8d8e8'}
            onMouseLeave={e => e.currentTarget.style.color = '#3a6080'}
          >← PubSilo</Link>
        </div>
      </div>

    </div>
    </>
  );
}
